import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { iso31662 } from 'iso-3166';
import { intersection } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Verification } from 'dashboard/models/verification';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  CheckFilterValues,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
  VerificationsFilterParams,
} from 'dashboard/redux/actions/verifications';
import { getInitialVerificationsFilterState } from 'dashboard/redux/reducers/verifications';
import {
  getAllTemplates,
  getAllVerificationTemplateFilterOptions,
  getAllVerificationTemplates,
} from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import { loadVerificationTemplateFilterOptions } from 'dashboard/redux/thunk-actions/organization';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { loadVerificationsList } from 'dashboard/redux/thunk-actions/verifications';
import DashboardVerificationsView from 'dashboard/views/DashboardVerificationsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { updateDateFilters } from 'lib/utils/filters';
import { VerificationStatus } from 'lib/verification';

const SUBDIVISIONS_BY_COUNTRY = iso31662.reduce(
  (countrySubdivisions: { [x: string]: any }, subdivision: { parent: string | number }) => {
    countrySubdivisions[subdivision.parent] = [
      ...(countrySubdivisions[subdivision.parent] || []),
      subdivision,
    ];
    return countrySubdivisions;
  },
  {},
);

const COUNTRIES_SUPPORTING_SUBDIVISIONS = ['US', 'CA', 'AU'];

function govIdIssuingSubvisions(countryCodes: string[]): Array<{ label: string; value: string }> {
  const countries = countryCodes.length ? countryCodes : COUNTRIES_SUPPORTING_SUBDIVISIONS;
  return intersection(countries, COUNTRIES_SUPPORTING_SUBDIVISIONS)
    .flatMap((countryCode) => SUBDIVISIONS_BY_COUNTRY[countryCode] || [])
    .map((subdivision) => {
      return {
        label: subdivision.name,
        value: subdivision.code,
      };
    });
}

const DashboardVerificationsRoute = (props: PropsFromRedux) => {
  const pageSize = props.pageSize;

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadVerificationsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
  );

  const { loading: loadingVerifications } = useApiLoader({
    loader: () =>
      props.loadVerificationsList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        abortSignal: abortController.signal,
      }),
    deps: [
      props.loadVerificationsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verifications.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    abortController: abortController,
  });

  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Verification),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingVerificationTemplateFilterOptions } = useApiLoader({
    loader: async () =>
      await props.loadVerificationTemplateFilterOptions({
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      }),
    deps: [props.loadVerificationTemplateFilterOptions],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load verification templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    handlePageLinkClick,
    handlePageSizeClick,
    handleDateUpdate,
    handleOmniSearch,
    handleSortUpdate,
  } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  // If we add another filter remember to update the default filters! front-end/dashboard/redux/reducers/verifications.ts
  const handleChecksFilterUpdate = (checks: CheckFilterValues[]) => {
    props.setFilters({ checks });
    props.setCurrentPage(1);
  };

  const handleStatusFilterUpdate = (status: VerificationStatus[]) => {
    props.setFilters({ status });
    props.setCurrentPage(1);
  };

  const handleTypeFilterUpdate = (type: Verification['type'][]) => {
    props.setFilters({ type });
    props.setCurrentPage(1);
  };

  const handleGovIdTypeFilterUpdate = (governmentIdTypes: string[]) => {
    props.setFilters({ governmentIdTypes });
    props.setCurrentPage(1);
  };

  const handleTagIdFilterUpdate = (tagIds: string[]) => {
    props.setFilters({ tagIds });
    props.setCurrentPage(1);
  };

  const handleIssuingCountriesFilterUpdate = (issuingCountries: string[]) => {
    props.setFilters({ issuingCountries });
    props.setCurrentPage(1);
  };

  const handleIssuingSubdivisionsFilterUpdate = (issuingSubdivisions: string[]) => {
    props.setFilters({ issuingSubdivisions });
    props.setCurrentPage(1);
  };

  const handleInquiryTemplateFilterUpdate = (inquiryTemplateIds: string[]) => {
    props.setFilters({ inquiryTemplateIds });
    props.setCurrentPage(1);
  };

  const handleVerificationTemplateFilterUpdate = (verificationTemplateIds: string[]) => {
    props.setFilters({ verificationTemplateIds });
    props.setCurrentPage(1);
  };

  // Initially reload the verifications filters state createdAtStart and createdAtEnd
  useEffect(() => {
    const initialFilters: VerificationsFilterParams = getInitialVerificationsFilterState();
    props.setFilters({
      createdAtStart: initialFilters.createdAtStart,
      createdAtEnd: initialFilters.createdAtEnd,
    });
  }, [props.setFilters]);

  return (
    <DashboardVerificationsView
      verifications={props.verifications}
      tags={props.tags}
      loadingVerifications={loadingVerifications}
      loadingTags={loadingTags}
      loadingVerificationTemplateFilterOptions={loadingVerificationTemplateFilterOptions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      availableSubdivisions={props.availableSubdivisions}
      inquiryTemplates={props.inquiryTemplates}
      verificationTemplateFilterOptions={props.verificationTemplatesFilterOptions}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
      handleChecksFilterUpdate={handleChecksFilterUpdate}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleIssuingCountriesFilterUpdate={handleIssuingCountriesFilterUpdate}
      handleIssuingSubdivisionsFilterUpdate={handleIssuingSubdivisionsFilterUpdate}
      handleGovIdTypeFilterUpdate={handleGovIdTypeFilterUpdate}
      handleTagIdFilterUpdate={handleTagIdFilterUpdate}
      handleTypeFilterUpdate={handleTypeFilterUpdate}
      handleInquiryTemplateFilterUpdate={handleInquiryTemplateFilterUpdate}
      handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    verifications:
      state.verifications.verifications[state.currentEnvironment.currentEnvironment.id] || [],
    tags: state.tags.tags.verification,
    paginationParams: state.verifications.paginationParams,
    sortParams: state.verifications.sortParams,
    filters: state.verifications.filters,
    currentPage: state.verifications.currentPage,
    pageSize: state.verifications.pageSize,
    featureFlags: state.featureFlags.featureFlags,
    availableSubdivisions: govIdIssuingSubvisions(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      state.verifications.filters.issuingCountries || [],
    ),
    inquiryTemplates: getAllTemplates(state),
    verificationTemplates: getAllVerificationTemplates(state),
    verificationTemplatesFilterOptions: getAllVerificationTemplateFilterOptions(state),
  };
};

const mapDispatchToProps = {
  loadVerificationsList,
  loadVerificationTemplateFilterOptions,
  loadTags,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationsRoute);
